import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Alert, Table, Badge, Modal, Form } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import TenantManagementLayout from './TenantManagementLayout';

const TenantUsers = ({ auth }) => {
  const { id } = useParams();
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'user'
  });
  const [inviting, setInviting] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch tenant details and invitations in parallel
      const [tenantResponse, invitationsResponse] = await Promise.all([
        fetch(`/api/admin/tenants/${id}`, { credentials: 'include' }),
        fetch(`/api/admin/tenants/${id}/invitations`, { credentials: 'include' })
      ]);

      if (tenantResponse.ok) {
        const data = await tenantResponse.json();
        setTenant(data.tenant);
        // API returns memberships with nested user data
        setUsers(data.tenant.memberships?.filter(m => m.is_active) || []);
      }

      if (invitationsResponse.ok) {
        const invData = await invitationsResponse.json();
        setInvitations(invData.invitations || []);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async (invitationId, email) => {
    setError(null);
    setResendingId(invitationId);

    try {
      const response = await fetch(`/api/admin/tenants/${id}/invitations/${invitationId}/resend`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Invitation resent to ${email}`);
        fetchData();
      } else {
        setError(data.error || 'Failed to resend invitation');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setResendingId(null);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setError(null);

    if (!inviteData.email) {
      setError('Email is required');
      return;
    }

    try {
      setInviting(true);

      // Platform admins can invite users to any tenant
      const response = await fetch(`/api/admin/tenants/${id}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(inviteData)
      });

      if (response.ok) {
        setSuccess(`Invitation sent to ${inviteData.email}`);
        setShowInviteModal(false);
        setInviteData({ email: '', role: 'user' });
        fetchData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to send invitation');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setInviting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString();
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'manager': return 'warning';
      default: return 'primary';
    }
  };

  if (loading) {
    return (
      <TenantManagementLayout title="Tenant Users">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading users...</p>
        </div>
      </TenantManagementLayout>
    );
  }

  if (!tenant) {
    return (
      <TenantManagementLayout title="Tenant Users">
        <Alert variant="warning">Tenant not found</Alert>
      </TenantManagementLayout>
    );
  }

  return (
    <TenantManagementLayout
      title={`Users - ${tenant.name}`}
      subtitle={`${users.length} of ${tenant.seat_count} seats used`}
    >
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
          {success}
        </Alert>
      )}

      {/* Quick Stats */}
      <Row className="mb-4">
        <Col md={4}>
          <Card style={{ background: '#f0fdf4', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#166534' }}>
                {users.length}
              </div>
              <div style={{ color: '#166534', fontSize: '0.9rem' }}>Active Users</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card style={{ background: '#eff6ff', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e40af' }}>
                {tenant.seat_count - users.length}
              </div>
              <div style={{ color: '#1e40af', fontSize: '0.9rem' }}>Available Seats</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card style={{ background: '#fef3c7', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#92400e' }}>
                {invitations.length}
              </div>
              <div style={{ color: '#92400e', fontSize: '0.9rem' }}>Pending Invites</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Users Table */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Users</h5>
          {users.length < tenant.seat_count && ['super_admin', 'admin'].includes(auth.platform_role) && (
            <Button variant="success" size="sm" onClick={() => setShowInviteModal(true)}>
              + Invite User
            </Button>
          )}
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th style={{ padding: '1rem' }}>User</th>
                <th style={{ padding: '1rem' }}>Display Name</th>
                <th style={{ padding: '1rem' }}>Role</th>
                <th style={{ padding: '1rem' }}>Joined</th>
                <th style={{ padding: '1rem' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-4 text-muted">
                    No users in this tenant
                  </td>
                </tr>
              ) : (
                users.map((membership, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '1rem' }}>
                      <div>
                        <span style={{ fontWeight: '500' }}>
                          {membership.user?.first_name} {membership.user?.last_name}
                        </span>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                          {membership.user?.email}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {membership.display_name || '-'}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <Badge bg={getRoleBadgeVariant(membership.role)}>
                        {membership.role}
                      </Badge>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {formatDate(membership.created_at)}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {membership.is_active ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="secondary">Inactive</Badge>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Pending Invitations</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ padding: '1rem' }}>Email</th>
                  <th style={{ padding: '1rem' }}>Role</th>
                  <th style={{ padding: '1rem' }}>Invited By</th>
                  <th style={{ padding: '1rem' }}>Expires</th>
                  <th style={{ padding: '1rem' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((inv, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '1rem' }}>{inv.email}</td>
                    <td style={{ padding: '1rem' }}>
                      <Badge bg={getRoleBadgeVariant(inv.role)}>
                        {inv.role}
                      </Badge>
                    </td>
                    <td style={{ padding: '1rem' }}>{inv.invitedByUser?.email || '-'}</td>
                    <td style={{ padding: '1rem' }}>{formatDate(inv.expires_at)}</td>
                    <td style={{ padding: '1rem' }}>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleResendInvitation(inv.id, inv.email)}
                        disabled={resendingId === inv.id}
                      >
                        {resendingId === inv.id ? 'Sending...' : 'Resend'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <div className="mt-4">
        <Link to={`/admin/tenants/${id}`} className="btn btn-outline-secondary">
          Back to Tenant Details
        </Link>
      </div>

      {/* Invite Modal */}
      <Modal show={showInviteModal} onHide={() => setShowInviteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Invite User to {tenant.name}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleInvite}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                value={inviteData.email}
                onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                placeholder="user@example.com"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={inviteData.role}
                onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowInviteModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={inviting}>
              {inviting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </TenantManagementLayout>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(TenantUsers);
